// "On this map": what the walker has switched on over the base map, and the rules for
// clearing it in two taps and putting it back.
//
// Three groups, each of which persists between visits:
//   pois      POI types               (mapDataPOIs.activatedPoiTypes)
//   overlays  data and weather layers (mapDataOverlays flags + activeWeatherLayer)
//   hikes     the walker's own recordings / planned / completed hikes as overlay lines
//             (mapDataOverlays.activeSources)
//
// Every item also carries a `switchedOnAt` timestamp, stored beside the selection it
// belongs to, so the control can show the most recently switched-on items first. Items
// switched on before the timestamps existed have none; they sort as the oldest, in the
// order their selection already keeps.
//
// Pure and free of mapbox-gl/Vuex so the whole thing runs under Jest. The store-facing
// half (snapshot, clear, restore) lives in apps/map/onThisMap/onThisMapController.ts.

export type OnThisMapGroup = 'pois' | 'overlays' | 'hikes';

export const ON_THIS_MAP_GROUPS: readonly OnThisMapGroup[] = ['pois', 'overlays', 'hikes'];

/** localStorage keys for the timestamps, one beside each module's persisted selection. */
export const POI_SWITCHED_ON_AT_KEY = 'activatedPoiTypesSwitchedOnAt';
export const OVERLAY_SWITCHED_ON_AT_KEY = 'mapOverlaysSwitchedOnAt';

export type SwitchedOnAt = Record<string, number>;

/** The boolean overlays: the store flag that holds each one and the action that toggles it. */
export const OVERLAY_TOGGLES = [
  { key: 'heatmap', flag: 'heatmapActive', toggle: 'toggleHeatmapOverlay' },
  { key: 'slope', flag: 'slopeAngleActive', toggle: 'toggleSlopeOverlay' },
  { key: 'cell', flag: 'cellCoverageActive', toggle: 'toggleCellCoverageOverlay' },
  { key: 'officialHikes', flag: 'officialHikesActive', toggle: 'toggleOfficialHikesOverlay' },
  { key: 'landAccess', flag: 'landAccessActive', toggle: 'toggleLandAccessOverlay' },
  { key: 'cycling', flag: 'cyclingRoutesActive', toggle: 'toggleCyclingRoutesOverlay' },
] as const;

export type OverlayFlag = typeof OVERLAY_TOGGLES[number]['flag'];

/** Weather layers are one-at-a-time; each is its own item, keyed by its store name. */
export const WEATHER_NAMES = ['temperature', 'cloud', 'rainfall', 'snowfall', 'wind'] as const;

export const weatherKey = (name: string): string => `weather:${name}`;

/** The walker's own hikes, drawn as overlay lines. Source ids double as item keys. */
export const HIKE_SOURCES = [
  'get_user_recordings',
  'get_user_planned_trails',
  'get_user_completed_hikes',
] as const;

/** Every timestamp key the overlays module manages, by group. */
export const OVERLAY_GROUP_KEYS: readonly string[] = [
  ...OVERLAY_TOGGLES.map((o) => o.key),
  ...WEATHER_NAMES.map(weatherKey),
];
export const HIKE_GROUP_KEYS: readonly string[] = [...HIKE_SOURCES];

export interface WeatherLayer { name: string | null; type: string | null }

export type OverlayFlags = Record<OverlayFlag, boolean> & { activeWeatherLayer: WeatherLayer };

/** The slice of store state this feature reads. */
export interface OnThisMapState {
  poiTypes: string[];
  poiSwitchedOnAt: SwitchedOnAt;
  overlays: OverlayFlags;
  activeSources: string[];
  switchedOnAt: SwitchedOnAt;
}

export interface OnThisMapItem {
  group: OnThisMapGroup;
  /** POI mapbox filter type, overlay key (`slope`, `weather:rainfall`) or hike source id. */
  key: string;
  /** 0 when the item predates timestamps. */
  switchedOnAt: number;
  /** Position within its own selection, the tie-break between equal timestamps. */
  seq: number;
}

// ─── Timestamps ──────────────────────────────────────────────────────────────

/** Stamps `key` with `now` unless it already has a stamp. Returns a new object on change. */
export function stampOn(stamps: SwitchedOnAt, key: string, now: number): SwitchedOnAt {
  return key in stamps ? stamps : { ...stamps, [key]: now };
}

export function stampOff(stamps: SwitchedOnAt, key: string): SwitchedOnAt {
  if (!(key in stamps)) return stamps;
  const next = { ...stamps };
  delete next[key];
  return next;
}

/**
 * Brings the stamps for `managedKeys` in line with what is now on: keeps the stamp of
 * anything still on, stamps anything newly on with `now`, drops the rest. Keys outside
 * `managedKeys` are left alone, so one map can hold several groups.
 */
export function syncStamps(
  stamps: SwitchedOnAt,
  managedKeys: readonly string[],
  activeKeys: readonly string[],
  now: number,
): SwitchedOnAt {
  const next: SwitchedOnAt = {};
  Object.keys(stamps).forEach((key) => {
    if (!managedKeys.includes(key)) next[key] = stamps[key];
  });
  activeKeys.forEach((key) => {
    if (managedKeys.includes(key)) next[key] = key in stamps ? stamps[key] : now;
  });
  return next;
}

/** Sets the stamps for `keys` to exactly what `from` had for them — the undo path. */
export function restoreStamps(
  stamps: SwitchedOnAt,
  keys: readonly string[],
  from: SwitchedOnAt,
): SwitchedOnAt {
  const next = { ...stamps };
  keys.forEach((key) => {
    if (key in from) next[key] = from[key];
    else delete next[key];
  });
  return next;
}

/** Reads a stamp map from storage, dropping anything that isn't a finite number. */
export function readStamps(storageKey: string, storage: Storage | undefined = safeStorage()): SwitchedOnAt {
  try {
    const parsed = JSON.parse(storage?.getItem(storageKey) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const stamps: SwitchedOnAt = {};
    Object.keys(parsed).forEach((key) => {
      if (typeof parsed[key] === 'number' && Number.isFinite(parsed[key])) stamps[key] = parsed[key];
    });
    return stamps;
  } catch {
    return {};
  }
}

export function writeStamps(storageKey: string, stamps: SwitchedOnAt, storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.setItem(storageKey, JSON.stringify(stamps));
  } catch {
    // Storage full or blocked: the selection itself still persists, only the chip order is lost.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

// ─── What is on ──────────────────────────────────────────────────────────────

export function activeHikeSources(activeSources: readonly string[]): string[] {
  return activeSources.filter((src) => (HIKE_SOURCES as readonly string[]).includes(src));
}

/** Overlay item keys that are on, in a fixed order. */
export function activeOverlayKeys(overlays: OverlayFlags): string[] {
  const keys: string[] = OVERLAY_TOGGLES.filter((o) => overlays[o.flag]).map((o) => o.key);
  const weather = overlays.activeWeatherLayer;
  if (weather?.name && weather?.type) keys.push(weatherKey(weather.name));
  return keys;
}

/** Everything switched on, newest first. */
export function activeItems(state: OnThisMapState): OnThisMapItem[] {
  const items: OnThisMapItem[] = [];
  state.poiTypes.forEach((key, seq) => {
    items.push({ group: 'pois', key, seq, switchedOnAt: state.poiSwitchedOnAt[key] ?? 0 });
  });
  activeOverlayKeys(state.overlays).forEach((key, seq) => {
    items.push({ group: 'overlays', key, seq, switchedOnAt: state.switchedOnAt[key] ?? 0 });
  });
  activeHikeSources(state.activeSources).forEach((key, seq) => {
    items.push({ group: 'hikes', key, seq, switchedOnAt: state.switchedOnAt[key] ?? 0 });
  });
  // Stable sort: equal stamps keep group order, and within a group the later entry wins
  // (selections append, so a later position means switched on later).
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) =>
      b.item.switchedOnAt - a.item.switchedOnAt ||
      (a.item.group === b.item.group ? b.item.seq - a.item.seq : a.index - b.index))
    .map(({ item }) => item);
}

export function itemsByGroup(items: readonly OnThisMapItem[]): Record<OnThisMapGroup, OnThisMapItem[]> {
  const groups: Record<OnThisMapGroup, OnThisMapItem[]> = { pois: [], overlays: [], hikes: [] };
  items.forEach((item) => groups[item.group].push(item));
  return groups;
}

export type MapsSheetTab = 'maps' | 'overlays' | 'pois';

/** Bus event asking the page's Maps sheet to open on a tab: payload `{ tab, dialogId? }`. */
export const SHOW_MAPS_SHEET_TAB_EVENT = 'map-styles-dialog:show-tab';

/** The Maps sheet tab where a group's items are switched on. Own hikes live under Overlays. */
export function mapsSheetTabFor(group: OnThisMapGroup): MapsSheetTab {
  return group === 'pois' ? 'pois' : 'overlays';
}

/** "a, b, c…" — the first `max` names, with an ellipsis when there are more. */
export function summariseNames(names: readonly string[], max = 3): string {
  const shown = names.slice(0, max).join(', ');
  return names.length > max ? `${shown}…` : shown;
}

// ─── Chips ───────────────────────────────────────────────────────────────────

export const CHIP_LIMIT = 3;

export type ChipGlyph = { symbol: string } | { svgPath: string };

// Slope and rain glyphs from the design handoff; the rest are Material Symbols (filled),
// the icon font the site already loads.
const SLOPE_PATH = 'M2 20h20V5z';

export const OVERLAY_CHIP_STYLES: Record<string, { fill: string; glyph: ChipGlyph }> = {
  slope: { fill: '#F57700', glyph: { svgPath: SLOPE_PATH } },
  cell: { fill: '#4BC263', glyph: { symbol: 'signal_cellular_alt' } },
  officialHikes: { fill: '#DB2B35', glyph: { symbol: 'hiking' } },
  landAccess: { fill: '#A7CF4B', glyph: { symbol: 'fence' } },
  cycling: { fill: '#0087C1', glyph: { symbol: 'directions_bike' } },
  heatmap: { fill: '#F07926', glyph: { symbol: 'local_fire_department' } },
  'weather:temperature': { fill: '#DB2B35', glyph: { symbol: 'device_thermostat' } },
  'weather:cloud': { fill: '#505259', glyph: { symbol: 'cloud' } },
  'weather:rainfall': { fill: '#0087C1', glyph: { symbol: 'rainy' } },
  'weather:snowfall': { fill: '#ACB0BF', glyph: { symbol: 'ac_unit' } },
  'weather:wind': { fill: '#2D2F35', glyph: { symbol: 'air' } },
  get_user_recordings: { fill: '#C37098', glyph: { symbol: 'radio_button_checked' } },
  get_user_planned_trails: { fill: '#FDDF00', glyph: { symbol: 'route' } },
  get_user_completed_hikes: { fill: '#4BC263', glyph: { symbol: 'check' } },
};

export const GLYPH_LIGHT = '#FFFFFF';
export const GLYPH_DARK = '#17181D';

/** WCAG relative luminance of a #RRGGBB colour. */
export function relativeLuminance(hex: string): number {
  const value = hex.replace('#', '');
  const channel = (offset: number) => {
    const c = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * White glyph unless it fails 3:1 on the fill (the WCAG bar for graphical objects), in
 * which case the dark one. At 12px a glyph is a graphic, not text.
 */
export function glyphColorFor(fill: string): string {
  return contrastRatio(GLYPH_LIGHT, fill) >= 3 ? GLYPH_LIGHT : GLYPH_DARK;
}

// ─── Layout ──────────────────────────────────────────────────────────────────

/**
 * How far to slide the Maps/Options row right so the Maps pill keeps its place when the
 * segment widens it. The row is centred, so growing by `segmentWidth` would otherwise move
 * Maps left by half of that. Capped by the room to the right, so on a narrow screen the row
 * never runs off the edge — it re-centres instead.
 */
export function anchoredRowShift(segmentWidth: number, roomRight: number): number {
  return Math.max(0, Math.min(segmentWidth / 2, roomRight));
}

// ─── Undo toast ──────────────────────────────────────────────────────────────

export const UNDO_TOAST_MIN_MS = 10_000;
export const RESTORED_TOAST_MS = 4_000;

/**
 * The undo toast stays for at least `minMs`, and also until the next map interaction —
 * whichever comes later. So: dismiss once both have happened.
 */
export function createStickyDismissTimer(options: {
  minMs: number;
  onDismiss: () => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}) {
  const setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  let handle: unknown = null;
  let running = false;
  let elapsed = false;
  let interacted = false;

  const stop = () => {
    if (handle !== null) clearTimer(handle);
    handle = null;
    running = false;
  };
  const dismiss = () => {
    stop();
    options.onDismiss();
  };

  return {
    start() {
      stop();
      running = true;
      elapsed = false;
      interacted = false;
      handle = setTimer(() => {
        handle = null;
        elapsed = true;
        if (interacted) dismiss();
      }, options.minMs);
    },
    /** A pan, zoom or tap on the map. */
    interact() {
      if (!running) return;
      interacted = true;
      if (elapsed) dismiss();
    },
    cancel: stop,
    get running() { return running; },
  };
}
